import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import { Badge } from './ui/badge';
import { 
  Bell, 
  Clock, 
  ClipboardCheck,
  Calendar, 
  AlertTriangle,
  FolderKanban,
  UserX,
  CheckCheck,
  X,
  ChevronRight,
  Settings,
  ExternalLink
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminNotificationBell = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const pollIntervalRef = useRef(null);

  // Only show for admin/manager
  const isAdminOrManager = user && ['admin', 'manager'].includes(user.role);

  // Fetch admin notifications
  const fetchNotifications = async () => {
    if (!token || !isAdminOrManager) return;
    try {
      const [notifRes, countRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/notifications?limit=20`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/admin/notifications/unread-count`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      const notifData = await notifRes.json();
      const countData = await countRes.json();
      setNotifications(notifData);
      setUnreadCount(countData.count);
      setHasMore(countData.has_more);
    } catch (error) {
      console.error('Failed to fetch admin notifications:', error);
    }
  };

  // Poll for new notifications every 30 seconds
  useEffect(() => {
    if (!isAdminOrManager) return;
    fetchNotifications();
    pollIntervalRef.current = setInterval(fetchNotifications, 30000);
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [token, isAdminOrManager]);

  // Mark single notification as read
  const markAsRead = async (notificationId) => {
    try {
      await fetch(`${API_URL}/api/admin/notifications/${notificationId}/read`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    setLoading(true);
    try {
      await fetch(`${API_URL}/api/admin/notifications/mark-all-read`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle notification click
  const handleNotificationClick = (notification) => {
    if (!notification.is_read) {
      markAsRead(notification.id);
    }
    if (notification.action_url) {
      setIsOpen(false);
      navigate(notification.action_url);
    }
  };

  // Delete notification
  const deleteNotification = async (e, notificationId) => {
    e.stopPropagation();
    try {
      await fetch(`${API_URL}/api/admin/notifications/${notificationId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      const deletedNotif = notifications.find(n => n.id === notificationId);
      if (deletedNotif && !deletedNotif.is_read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  };

  // Get icon for notification type
  const getIcon = (icon, type) => {
    switch (icon || type) {
      case 'clipboard':
      case 'timesheet_submitted':
        return <ClipboardCheck className="w-4 h-4" />;
      case 'calendar':
      case 'time_off_request':
        return <Calendar className="w-4 h-4" />;
      case 'clock':
      case 'employee_not_tracking':
        return <Clock className="w-4 h-4" />;
      case 'alert':
      case 'timesheet_overdue':
      case 'escalation':
        return <AlertTriangle className="w-4 h-4" />;
      case 'folder':
      case 'project_over_budget':
        return <FolderKanban className="w-4 h-4" />;
      case 'user':
        return <UserX className="w-4 h-4" />;
      default:
        return <Bell className="w-4 h-4" />;
    }
  };

  // Get icon background color based on type
  const getIconBg = (type, priority) => {
    if (priority === 'urgent' || priority === 'high') {
      return 'bg-red-100 text-red-600 dark:bg-red-900/20 dark:text-red-400';
    }
    switch (type) {
      case 'timesheet_submitted':
        return 'bg-blue-100 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400';
      case 'time_off_request':
        return 'bg-purple-100 text-purple-600 dark:bg-purple-900/20 dark:text-purple-400';
      case 'employee_not_tracking':
        return 'bg-amber-100 text-amber-600 dark:bg-amber-900/20 dark:text-amber-400';
      case 'timesheet_overdue':
        return 'bg-orange-100 text-orange-600 dark:bg-orange-900/20 dark:text-orange-400';
      case 'project_over_budget':
        return 'bg-red-100 text-red-600 dark:bg-red-900/20 dark:text-red-400';
      case 'escalation':
        return 'bg-rose-100 text-rose-600 dark:bg-rose-500/20 dark:text-rose-400';
      default:
        return 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:bg-slate-700 dark:text-slate-400';
    }
  };

  // Get priority badge
  const getPriorityBadge = (priority) => {
    if (!priority || priority === 'normal') return null;
    const colors = {
      low: 'bg-slate-100 dark:bg-slate-700 text-slate-600',
      high: 'bg-orange-100 text-orange-700',
      urgent: 'bg-red-100 text-red-700'
    };
    return (
      <Badge className={`text-[9px] px-1.5 py-0 ${colors[priority] || ''}`}>
        {priority.toUpperCase()}
      </Badge>
    );
  };

  if (!isAdminOrManager) {
    return null;
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <button 
          className="relative p-2 rounded-xl hover:bg-purple-100 dark:hover:bg-purple-50 dark:bg-purple-900/20 transition-colors"
          data-testid="admin-notification-bell"
        >
          <Bell className="w-5 h-5 text-purple-600 dark:text-purple-400" />
          {unreadCount > 0 && (
            <span 
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center px-1 text-[10px] font-bold text-white bg-red-500 dark:bg-red-500 rounded-full"
              data-testid="admin-notification-badge"
            >
              {hasMore ? '99+' : unreadCount}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent 
        className="w-[420px] p-0 rounded-2xl shadow-xl" 
        align="end"
        data-testid="admin-notification-dropdown"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-100 dark:border-slate-800 bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-500/10 dark:to-indigo-500/10 rounded-t-2xl">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-purple-900 dark:text-purple-100">
              Admin Notifications
            </h3>
            <Badge className="bg-purple-200 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 text-[10px]">
              Admin
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={markAllAsRead}
                disabled={loading}
                className="h-7 text-xs text-purple-600 hover:text-purple-700 hover:bg-purple-100 dark:hover:bg-purple-50 dark:bg-purple-900/20"
                data-testid="admin-mark-all-read-btn"
              >
                <CheckCheck className="w-3.5 h-3.5 mr-1" />
                Mark all read
              </Button>
            )}
            <button
              onClick={() => { setIsOpen(false); navigate('/admin/notifications'); }}
              className="p-1.5 rounded-lg hover:bg-purple-100 dark:hover:bg-purple-50 dark:bg-purple-900/20 text-purple-500 hover:text-purple-700"
              title="View All Notifications"
            >
              <ExternalLink className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Notifications List */}
        <div className="max-h-[400px] overflow-y-auto">
          {notifications.length === 0 ? (
            <div className="py-12 text-center">
              <Bell className="w-10 h-10 text-purple-200 dark:text-purple-800 mx-auto mb-3" />
              <p className="text-sm text-slate-500">No admin notifications</p>
              <p className="text-xs text-slate-400 mt-1">You'll be notified about team activity here</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100 dark:divide-slate-800">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`flex items-start gap-3 p-4 cursor-pointer transition-colors group ${
                    notification.is_read 
                      ? 'bg-white dark:bg-slate-900 hover:bg-slate-50 dark:hover:bg-slate-800/50' 
                      : 'bg-purple-50 dark:bg-purple-900/30/50 dark:bg-purple-900/5 hover:bg-purple-50 dark:hover:bg-purple-50 dark:bg-purple-900/10'
                  }`}
                  data-testid={`admin-notification-${notification.id}`}
                >
                  {/* Icon */}
                  <div className={`flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center ${getIconBg(notification.type, notification.priority)}`}>
                    {getIcon(notification.icon, notification.type)}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className={`text-sm font-medium ${notification.is_read ? 'text-slate-700 dark:text-slate-300' : 'text-slate-900 dark:text-white'}`}>
                        {notification.title}
                      </p>
                      {getPriorityBadge(notification.priority)}
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">
                      {notification.message}
                    </p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-[10px] text-slate-400">
                        {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                      </span>
                      {notification.action_label && (
                        <span className="flex items-center text-[10px] text-purple-600 dark:text-purple-400 font-medium">
                          {notification.action_label}
                          <ChevronRight className="w-3 h-3" />
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Unread dot / Delete button */}
                  <div className="flex-shrink-0">
                    {!notification.is_read ? (
                      <div className="w-2 h-2 rounded-full bg-purple-500 dark:bg-purple-400" />
                    ) : (
                      <button
                        onClick={(e) => deleteNotification(e, notification.id)}
                        className="opacity-0 group-hover:opacity-100 p-1 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-400 transition-opacity"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {notifications.length > 0 && (
          <div className="p-3 border-t border-slate-100 dark:border-slate-800">
            <Button
              variant="ghost"
              className="w-full h-9 text-xs text-purple-600 hover:text-purple-700 hover:bg-purple-50 dark:text-purple-400 dark:hover:bg-purple-50 dark:bg-purple-900/10"
              onClick={() => { setIsOpen(false); navigate('/admin/notifications'); }}
            >
              View all notifications
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default AdminNotificationBell;
